/**
 * Pixel budget for a column header to fit the title plus its drag handle and
 * filter/sort affordances at the table's initial (unresized) layout.
 *
 * The values mirror what `ColumnHeader` and `DataTable` actually render:
 *   - 16px cell padding (`px: 1` × 2 in `DriverDataGrid`)
 *   - 20px drag handle (16px icon + 4px gap), when `draggable`
 *   - 30px filter button (18px icon + IconButton padding + space-between gap)
 *   - 30px sort dropdown (same shape as filter button), when `sortable`
 *   - 20px buffer for resize handle, font kerning, and rounding
 */
const CELL_PADDING_PX = 16;
const DRAG_HANDLE_PX = 20;
const FILTER_BUTTON_PX = 30;
const SORT_DROPDOWN_PX = 30;
const BUFFER_PX = 20;

/**
 * Empirical px-per-char for the header label (14px Inter at weight 600). Slightly
 * generous so non-Latin and capital-heavy labels still fit without truncation.
 */
const TEXT_PX_PER_CHAR = 10;

export type ColumnHeaderAffordances = {
  draggable?: boolean;
  hasFilter?: boolean;
  sortable?: boolean;
};

/**
 * Minimum column width in pixels so the header title and its filter/sort
 * controls remain fully visible on first load, before any user resize.
 */
export function minWidthForColumnHeader(label: string, affordances: ColumnHeaderAffordances = {}): number {
  const { draggable = true, hasFilter = true, sortable = false } = affordances;
  const fixed =
    CELL_PADDING_PX +
    (draggable ? DRAG_HANDLE_PX : 0) +
    (hasFilter ? FILTER_BUTTON_PX : 0) +
    (sortable ? SORT_DROPDOWN_PX : 0) +
    BUFFER_PX;
  return Math.ceil(label.length * TEXT_PX_PER_CHAR) + fixed;
}
